import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { AppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const RELATIONSHIP_OPTIONS = [
  { value: 'synced', label: 'Synced', emoji: '🔗' },
  { value: 'dating', label: 'In a Relationship', emoji: '❤️' },
  { value: 'engaged', label: 'Engaged', emoji: '💍' },
  { value: 'married', label: 'Married', emoji: '💒' },
  { value: 'in-a-relationship', label: 'Together', emoji: '💕' },
];

const Profile = () => {
  const { user, profile, logout, isAdmin, updateProfile } = useContext(AuthContext);
  const { invitations } = useContext(AppContext);
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showPartnerSearch, setShowPartnerSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAnniversaryPicker, setShowAnniversaryPicker] = useState(false);
  const [anniversaryDate, setAnniversaryDate] = useState(profile?.anniversary_date || '');

  const sent = invitations.filter(i => i.direction === 'sent');
  const received = invitations.filter(i => i.direction === 'received');
  const moments = invitations.filter(i => i.status === 'confirmed' || i.status === 'replied');

  useEffect(() => {
    if (profile?.partner_id && supabase) {
      supabase
        .from('user_profiles')
        .select('name, avatar_url, username')
        .eq('id', profile.partner_id)
        .maybeSingle()
        .then(({ data }) => { if (data) setPartner(data); });
    }
  }, [profile?.partner_id]);

  const searchPartner = async (query) => {
    if (!query || query.length < 2 || !supabase) return;
    setSearching(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, name, avatar_url, username')
        .ilike('username', `%${query.toLowerCase()}%`)
        .neq('id', user?.id)
        .limit(5);
      setSearchResults(data || []);
    } catch (e) { /* ignore */ }
    setSearching(false);
  };

  const connectPartner = async (partnerUser) => {
    if (!supabase || !partnerUser?.id) return;
    // Link both profiles
    await updateProfile({ partner_id: partnerUser.id });
    // Also update partner's profile to link back
    await supabase
      .from('user_profiles')
      .update({ partner_id: user.id, updated_at: new Date().toISOString() })
      .eq('id', partnerUser.id);
    setPartner(partnerUser);
    setShowPartnerSearch(false);
    setShowStatusPicker(true);
  };

  const updateRelationshipStatus = async (status) => {
    await updateProfile({ relationship_status: status });
    // Also update partner
    if (profile?.partner_id) {
      await supabase
        .from('user_profiles')
        .update({ relationship_status: status, updated_at: new Date().toISOString() })
        .eq('id', profile.partner_id);
    }
    setShowStatusPicker(false);
  };

  const saveAnniversary = async () => {
    await updateProfile({ anniversary_date: anniversaryDate });
    if (profile?.partner_id) {
      await supabase
        .from('user_profiles')
        .update({ anniversary_date: anniversaryDate, updated_at: new Date().toISOString() })
        .eq('id', profile.partner_id);
    }
    setShowAnniversaryPicker(false);
  };

  const disconnectPartner = async () => {
    if (!confirm('Are you sure you want to disconnect?')) return;
    if (profile?.partner_id) {
      await supabase
        .from('user_profiles')
        .update({ partner_id: null, relationship_status: '', anniversary_date: null, updated_at: new Date().toISOString() })
        .eq('id', profile.partner_id);
    }
    await updateProfile({ partner_id: null, relationship_status: '', anniversary_date: null });
    setPartner(null);
  };

  if (!user) return null;

  const menuItems = [
    { icon: 'Edit3', label: 'Edit Profile', action: () => {} },
    { icon: 'Bell', label: 'Notifications', action: () => {} },
    { icon: 'Shield', label: 'Privacy', action: () => {} },
    { icon: 'Lock', label: 'Password', action: () => navigate('/forgot-password') },
    { icon: 'HelpCircle', label: 'Help & Support', action: () => {} },
    { icon: 'FileText', label: 'Terms & Privacy', action: () => {} },
  ];

  if (isAdmin) {
    menuItems.unshift({ icon: 'Settings', label: 'Admin Panel', action: () => navigate('/admin'), special: true });
  }

  const currentStatus = RELATIONSHIP_OPTIONS.find(r => r.value === profile?.relationship_status);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="bg-gray-50 min-h-full pb-8">
      <div className="bg-white px-6 py-8 text-center">
        <img
          src={user.avatar_url || user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
          alt={user.name}
          className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-pink-100"
        />
        <h2 className="text-xl font-bold text-gray-800">{user.name || 'User'}</h2>
        <p className="text-sm text-gray-500">{user.email}</p>
        {profile?.username && (
          <p className="text-sm text-pink-500 font-medium mt-1">@{profile.username}</p>
        )}
        {isAdmin && (
          <span className="inline-block mt-2 px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">Admin</span>
        )}

        {/* Relationship Status Badge */}
        {currentStatus && partner && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-50 rounded-full border border-pink-100">
            <span>{currentStatus.emoji}</span>
            <span className="text-xs font-bold text-pink-700">{currentStatus.label}</span>
          </div>
        )}

        {/* Anniversary */}
        {profile?.anniversary_date && (
          <p className="text-xs text-gray-400 mt-2 flex items-center justify-center gap-1">
            <SafeIcon name="Heart" className="text-pink-400 w-3 h-3" />
            Together since {formatDate(profile.anniversary_date)}
          </p>
        )}
      </div>

      {/* Partner Section */}
      {partner ? (
        <div className="mx-4 -mt-4 mb-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-4 border border-pink-100">
          <div className="flex items-center gap-3">
            <img
              src={partner.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${partner.name}`}
              alt={partner.name}
              className="w-14 h-14 rounded-full border-2 border-pink-200"
            />
            <div className="flex-1">
              <p className="text-xs text-pink-400 font-semibold uppercase">Your Partner</p>
              <p className="font-bold text-gray-800">{partner.name}</p>
              {partner.username && <p className="text-xs text-gray-500">@{partner.username}</p>}
            </div>
            <div className="flex gap-2">
              {!currentStatus && (
                <button onClick={() => setShowStatusPicker(true)} className="text-xs bg-pink-500 text-white px-3 py-1.5 rounded-lg font-medium">
                  Set Status
                </button>
              )}
              {!profile?.anniversary_date && (
                <button onClick={() => setShowAnniversaryPicker(true)} className="text-xs bg-purple-500 text-white px-3 py-1.5 rounded-lg font-medium">
                  Anniversary
                </button>
              )}
            </div>
          </div>

          {/* Shared Moments count */}
          <div className="mt-3 pt-3 border-t border-pink-100 flex items-center justify-center gap-2">
            <span className="text-2xl">💕</span>
            <span className="text-sm text-pink-600 font-medium">{moments.length} shared moments</span>
          </div>
        </div>
      ) : (
        <div className="mx-4 -mt-4 mb-4">
          <button
            onClick={() => setShowPartnerSearch(true)}
            className="w-full bg-white rounded-2xl p-4 border border-dashed border-pink-200 text-center hover:bg-pink-50 transition-colors"
          >
            <span className="text-2xl">💕</span>
            <p className="text-sm text-pink-500 font-medium mt-1">Connect with your partner</p>
            <p className="text-xs text-gray-400">Search their username to link profiles</p>
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 px-4 mb-6">
        {[
          { label: 'Sent', value: sent.length },
          { label: 'Received', value: received.length },
          { label: 'Moments', value: moments.length }
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-3 rounded-xl text-center shadow-sm">
            <div className="text-xl font-bold text-gray-800">{stat.value}</div>
            <div className="text-xs text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Menu */}
      <div className="bg-white rounded-t-3xl px-2 py-4">
        {menuItems.map((item) => (
          <button
            key={item.label}
            onClick={item.action}
            className={`w-full flex items-center gap-4 px-4 py-3.5 hover:bg-gray-50 rounded-xl transition-colors ${
              item.special ? 'bg-purple-50 border border-purple-100' : ''
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              item.special ? 'bg-purple-100' : 'bg-pink-50'
            }`}>
              <SafeIcon name={item.icon} className={item.special ? 'text-purple-500' : 'text-pink-500'} />
            </div>
            <span className={`flex-1 text-left font-medium ${item.special ? 'text-purple-700' : 'text-gray-700'}`}>{item.label}</span>
            <SafeIcon name="ChevronRight" className="text-gray-400" />
          </button>
        ))}

        <div className="px-4 pt-4 mt-4 border-t border-gray-100">
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="w-full flex items-center gap-4 py-3.5 text-red-500"
          >
            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
              <SafeIcon name="LogOut" className="text-red-500" />
            </div>
            <span className="font-medium">Logout</span>
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">DayClap v1.0.0</p>
      </div>

      {/* Partner Search Modal */}
      {showPartnerSearch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setShowPartnerSearch(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Find Your Partner 💕</h3>
            <div className="relative mb-4">
              <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text" value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); searchPartner(e.target.value); }}
                placeholder="Search by username..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none"
              />
            </div>
            {searching && <p className="text-sm text-gray-400 text-center py-4">Searching...</p>}
            {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
              <p className="text-sm text-gray-400 text-center py-4">No users found</p>
            )}
            {searchResults.map((result) => (
              <button
                key={result.id}
                onClick={() => connectPartner(result)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-pink-50 transition-colors mb-2"
              >
                <img src={result.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${result.name}`} alt="" className="w-10 h-10 rounded-full" />
                <div className="text-left">
                  <p className="font-semibold text-gray-800 text-sm">{result.name}</p>
                  <p className="text-xs text-gray-400">@{result.username}</p>
                </div>
              </button>
            ))}
            <button onClick={() => setShowPartnerSearch(false)} className="w-full mt-4 text-gray-500 font-medium py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Relationship Status Picker */}
      {showStatusPicker && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setShowStatusPicker(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Relationship Status 💕</h3>
            <div className="space-y-2">
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => updateRelationshipStatus(opt.value)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-pink-50 transition-colors border border-gray-100"
                >
                  <span className="text-2xl">{opt.emoji}</span>
                  <span className="font-medium text-gray-700">{opt.label}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShowStatusPicker(false)} className="w-full mt-4 text-gray-500 font-medium py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Anniversary Picker */}
      {showAnniversaryPicker && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setShowAnniversaryPicker(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Your Anniversary 💕</h3>
            <input
              type="date" value={anniversaryDate}
              onChange={(e) => setAnniversaryDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none mb-4"
            />
            <button
              onClick={saveAnniversary}
              disabled={!anniversaryDate}
              className="w-full bg-pink-500 text-white font-bold py-3 rounded-xl disabled:opacity-50"
            >
              Save Anniversary
            </button>
            <button onClick={() => setShowAnniversaryPicker(false)} className="w-full mt-2 text-gray-500 font-medium py-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;